import { getCurrencyLabel } from "@repo/shared";
import type { LocalizedString, PortfolioCollectionCalculationsMode } from "@repo/shared/types";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useFinancialSituationModels } from "@/lib/hooks/use-financial-situation-models";
import {
  usePortfolioCollection,
  useSustainabilityConfig,
  useUpdateCollection,
  useUpdateCollectionSettings,
  useUpdateMandateSettings,
  useUpdateSustainabilityConfig,
} from "@/lib/hooks/use-portfolio-collections";
import { useActiveRiskModels } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { DEFAULT_SFDR_QUESTION, DEFAULT_SFDR_SCORES } from "../sustainability/sfdr-defaults";
import { T } from "./collection-settings.i18n";

type CollectionSettingsProps = {
  portfolioCollectionId: string;
  currency: string;
  riskFreeRatePct: number;
  bankReturnPct: number;
  requiresAssetAllocation: boolean;
  requiresTimeseries: boolean;
  financialSituationModelId: string | null;
  enforceMinBuyAmounts: boolean;
  dynamicPortfoliosEnabled: boolean;
  dynamicPartialReplacementEnabled: boolean;
  allowModelPortfolioRiskDeviation: boolean;
  wrapperTypesEnabled: boolean;
  productTypesEnabled: boolean;
  calculationsMode: PortfolioCollectionCalculationsMode;
  requiresIsin: boolean;
  riskModelId: string | null;
  riskModel: { id: string; nameI18n: LocalizedString } | null;
};

export function CollectionSettings({
  portfolioCollectionId,
  currency,
  riskFreeRatePct,
  bankReturnPct,
  requiresAssetAllocation,
  requiresTimeseries,
  financialSituationModelId,
  enforceMinBuyAmounts,
  dynamicPortfoliosEnabled,
  dynamicPartialReplacementEnabled,
  allowModelPortfolioRiskDeviation,
  wrapperTypesEnabled,
  productTypesEnabled,
  calculationsMode,
  requiresIsin,
  riskModelId,
  riskModel,
}: CollectionSettingsProps) {
  const localize = useLocalize();
  const t = useT(T);
  const { userLanguage } = useUserLanguage();

  const updateSettings = useUpdateCollectionSettings();
  // Cached collection detail (same query the layout uses) — only isActive is
  // needed here, to explain a blocked dynamic-portfolios enable.
  const { collection } = usePortfolioCollection(portfolioCollectionId);
  const isCollectionActive = collection?.isActive ?? false;
  const { models: financialSituationModels } = useFinancialSituationModels();
  const { config: sustainabilityConfig } = useSustainabilityConfig(portfolioCollectionId);
  const updateSustainabilityConfig = useUpdateSustainabilityConfig();
  // QL-375 — the mandate switch, instant like sustainability; the rules live on
  // the Mandates tab the switch reveals.
  const updateMandateSettings = useUpdateMandateSettings();
  const mandatesEnabled = collection?.mandatesEnabled ?? false;
  const mandatesRequireAssetAllocation = !(collection?.requiresAssetAllocation ?? false);
  const handleMandatesToggle = (checked: boolean) => {
    setError(null);
    updateMandateSettings.mutate(
      { id: portfolioCollectionId, data: { mandatesEnabled: checked } },
      { onError: (err: Error) => setError(err.message || t.failedToToggleMandates) },
    );
  };
  const isPending = updateSettings.isPending;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [disableSustainabilityOpen, setDisableSustainabilityOpen] = useState(false);

  const sustainabilityEnabled = sustainabilityConfig != null;

  const handleSustainabilityToggle = (checked: boolean) => {
    if (checked) {
      // Turning ON: only seed a default when no config exists yet. The config
      // must be non-empty (at least one criterion) — the service normalises an
      // all-empty config back to null (disabled). We seed the SFDR criterion
      // with its three default Article 6/8/9 labels (immediately useful; SFDR is
      // resolved from each product's linked Morningstar instrument).
      if (sustainabilityConfig == null) {
        updateSustainabilityConfig.mutate({
          collectionId: portfolioCollectionId,
          config: {
            sfdr: { labelI18n: DEFAULT_SFDR_QUESTION, scores: DEFAULT_SFDR_SCORES },
            bars: [],
            productPreferences: [],
          },
        });
      }
    } else {
      // Turning OFF wipes the config — confirm first.
      setDisableSustainabilityOpen(true);
    }
  };

  const confirmDisableSustainability = () => {
    updateSustainabilityConfig.mutate({
      collectionId: portfolioCollectionId,
      config: null,
    });
  };

  // Risk model — set-once, assigned immediately via PATCH /:id (not part of Save).
  const updateCollection = useUpdateCollection();
  const { models: activeRiskModels } = useActiveRiskModels();
  // pendingRiskModelId doubles as the confirm dialog's open state.
  const [pendingRiskModelId, setPendingRiskModelId] = useState<string | null>(null);

  // The picker shows only active risk models. If the assigned model was
  // deactivated after assignment, include it so the disabled select can still
  // render its name.
  const displayedRiskModels: Array<{ id: string; nameI18n: LocalizedString }> =
    riskModel && !activeRiskModels.some((m) => m.id === riskModel.id)
      ? [...activeRiskModels, riskModel]
      : activeRiskModels;

  const confirmAssignRiskModel = () => {
    if (!pendingRiskModelId) return;
    setError(null);
    updateCollection.mutate(
      { id: portfolioCollectionId, data: { riskModelId: pendingRiskModelId } },
      { onError: (err) => setError(err.message || t.failedToAssignRiskModel) },
    );
  };

  const [formData, setFormData] = useState({
    riskFreeRatePct: riskFreeRatePct.toString(),
    bankReturnPct: bankReturnPct.toString(),
    requiresAssetAllocation,
    requiresTimeseries,
    financialSituationModelId,
    enforceMinBuyAmounts,
    dynamicPortfoliosEnabled,
    dynamicPartialReplacementEnabled,
    allowModelPortfolioRiskDeviation,
    wrapperTypesEnabled,
    productTypesEnabled,
    calculationsMode,
    requiresIsin,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const riskFreeRate = parseFloat(formData.riskFreeRatePct);

    if (Number.isNaN(riskFreeRate)) {
      setError(t.invalidRiskFreeRate);
      return;
    }

    const bankRate = parseFloat(formData.bankReturnPct);

    if (Number.isNaN(bankRate)) {
      setError(t.invalidBankRate);
      return;
    }

    updateSettings.mutate(
      {
        id: portfolioCollectionId,
        data: {
          riskFreeRatePct: riskFreeRate,
          bankReturnPct: bankRate,
          requiresAssetAllocation: formData.requiresAssetAllocation,
          requiresTimeseries: formData.requiresTimeseries,
          financialSituationModelId: formData.financialSituationModelId,
          enforceMinBuyAmounts: formData.enforceMinBuyAmounts,
          dynamicPortfoliosEnabled: formData.dynamicPortfoliosEnabled,
          dynamicPartialReplacementEnabled: formData.dynamicPartialReplacementEnabled,
          allowModelPortfolioRiskDeviation: formData.allowModelPortfolioRiskDeviation,
          wrapperTypesEnabled: formData.wrapperTypesEnabled,
          productTypesEnabled: formData.productTypesEnabled,
          calculationsMode: formData.calculationsMode,
          requiresIsin: formData.requiresIsin,
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
        },
        onError: (err) => {
          setError(err.message || t.failedToSave);
          // Nothing was persisted — snap the toggles back to the saved state so
          // the controls don't misrepresent what's stored (e.g. after a blocked
          // disable). Rate inputs are left as typed.
          setFormData((prev) => ({
            ...prev,
            requiresAssetAllocation,
            requiresTimeseries,
            financialSituationModelId,
            enforceMinBuyAmounts,
            dynamicPortfoliosEnabled,
            dynamicPartialReplacementEnabled,
            allowModelPortfolioRiskDeviation,
            wrapperTypesEnabled,
            productTypesEnabled,
            calculationsMode,
            requiresIsin,
          }));
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md whitespace-pre-line">
              {error}
            </div>
          )}

          {success && (
            <div className="text-sm text-success bg-success/10 p-3 rounded-md">
              {t.settingsSaved}
            </div>
          )}

          <div className="grid gap-6 sm:grid-cols-2">
            {/* Currency — chosen when the collection is created, fixed after (QL-372) */}
            <div className="space-y-2">
              <Label htmlFor="currency">{t.currency}</Label>
              <Input
                id="currency"
                value={getCurrencyLabel(currency, userLanguage)}
                disabled
                readOnly
              />
              <p className="text-xs text-muted-foreground">{t.currencyHint}</p>
            </div>

            {/* Risk-Free Rate */}
            <div className="space-y-2">
              <Label htmlFor="riskFreeRate">{t.riskFreeRate}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="riskFreeRate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.riskFreeRatePct}
                  onChange={(e) => setFormData({ ...formData, riskFreeRatePct: e.target.value })}
                  placeholder="2.5"
                  disabled={isPending}
                  className="w-24"
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">{t.riskFreeRateHint}</p>
            </div>

            {/* Bank Rate — assumption behind the forecast bank-savings comparison */}
            <div className="space-y-2">
              <Label htmlFor="bankRate">{t.bankRate}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="bankRate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.bankReturnPct}
                  onChange={(e) => setFormData({ ...formData, bankReturnPct: e.target.value })}
                  placeholder="2.0"
                  disabled={isPending}
                  className="w-24"
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">{t.bankRateHint}</p>
            </div>
          </div>

          {/* Product requirement toggles */}
          <div className="space-y-4">
            <p className="font-semibold leading-none tracking-tight">{t.requirementsTitle}</p>

            {/* Calculations mode (QP-164) — governs which entity's expected return, volatility
                and correlation data drive calculations. Locked while the collection is active. */}
            <div className="space-y-2">
              <Label htmlFor="calculationsMode">{t.calculationsMode}</Label>
              <Select
                value={formData.calculationsMode}
                onValueChange={(value) => {
                  setError(null);
                  setFormData({
                    ...formData,
                    calculationsMode: value as PortfolioCollectionCalculationsMode,
                  });
                }}
                disabled={isPending || isCollectionActive}
              >
                <SelectTrigger id="calculationsMode" className="sm:w-80">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset_classes">{t.calculationsModeAssetClasses}</SelectItem>
                  <SelectItem value="products">{t.calculationsModeProducts}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {isCollectionActive
                  ? t.calculationsModeCannotChangeWhileActive
                  : t.calculationsModeHint}
              </p>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="requiresAssetAllocation">{t.requiresAssetAllocation}</Label>
                <p className="text-xs text-muted-foreground">{t.requiresAssetAllocationHint}</p>
              </div>
              <Switch
                id="requiresAssetAllocation"
                checked={formData.requiresAssetAllocation}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requiresAssetAllocation: checked })
                }
                disabled={isPending}
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="requiresTimeseries">{t.requiresTimeseries}</Label>
                <p className="text-xs text-muted-foreground">{t.requiresTimeseriesHint}</p>
              </div>
              <Switch
                id="requiresTimeseries"
                checked={formData.requiresTimeseries}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requiresTimeseries: checked })
                }
                disabled={isPending}
              />
            </div>

            {/* Require ISIN (QP-164) — Products-mode only; hidden in Asset Classes mode. */}
            {formData.calculationsMode === "products" && (
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="requiresIsin">{t.requiresIsin}</Label>
                  <p className="text-xs text-muted-foreground">{t.requiresIsinHint}</p>
                </div>
                <Switch
                  id="requiresIsin"
                  checked={formData.requiresIsin}
                  onCheckedChange={(checked) => {
                    setError(null);
                    setFormData({ ...formData, requiresIsin: checked });
                  }}
                  disabled={isPending}
                />
              </div>
            )}

            {/* Sustainability — instant toggle (not tied to Save); wipes config when turned off */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="enableSustainability">{t.enableSustainability}</Label>
                <p className="text-xs text-muted-foreground">{t.enableSustainabilityHint}</p>
              </div>
              <Switch
                id="enableSustainability"
                checked={sustainabilityEnabled}
                onCheckedChange={handleSustainabilityToggle}
                disabled={updateSustainabilityConfig.isPending}
              />
            </div>

            {/* Mandates (QL-375) — instant toggle (not tied to Save); the Mandates tab appears when on */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="enableMandates">{t.enableMandates}</Label>
                <p className="text-xs text-muted-foreground">
                  {!mandatesEnabled && mandatesRequireAssetAllocation
                    ? t.enableMandatesRequiresAssetAllocation
                    : t.enableMandatesHint}
                </p>
              </div>
              <Switch
                id="enableMandates"
                checked={mandatesEnabled}
                onCheckedChange={handleMandatesToggle}
                disabled={
                  updateMandateSettings.isPending ||
                  (!mandatesEnabled && mandatesRequireAssetAllocation)
                }
              />
            </div>

            {/* Risk model — set-once, assigned immediately (not tied to Save) */}
            <div className="space-y-2">
              <Label htmlFor="riskModel">{t.riskModel}</Label>
              <Select
                value={riskModelId ?? ""}
                onValueChange={setPendingRiskModelId}
                disabled={riskModelId != null || updateCollection.isPending}
              >
                <SelectTrigger id="riskModel" className="sm:w-80">
                  <SelectValue placeholder={t.riskModelPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {displayedRiskModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {localize(model.nameI18n)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {riskModelId ? t.riskModelCannotChange : t.riskModelAssignHint}
              </p>
            </div>

            {/* FS-model relation — enables client-status conditions on platforms */}
            <div className="space-y-2">
              <Label htmlFor="financialSituationModel">{t.financialSituationModel}</Label>
              <Select
                value={formData.financialSituationModelId ?? "none"}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    financialSituationModelId: value === "none" ? null : value,
                  })
                }
                disabled={isPending}
              >
                <SelectTrigger id="financialSituationModel" className="sm:w-80">
                  <SelectValue placeholder={t.financialSituationModelNone} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.financialSituationModelNone}</SelectItem>
                  {financialSituationModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t.financialSituationModelHint}</p>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="enforceMinBuyAmounts">{t.enforceMinBuyAmounts}</Label>
                <p className="text-xs text-muted-foreground">{t.enforceMinBuyAmountsHint}</p>
              </div>
              <Switch
                id="enforceMinBuyAmounts"
                checked={formData.enforceMinBuyAmounts}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, enforceMinBuyAmounts: checked })
                }
                disabled={isPending}
              />
            </div>

            {/* Dynamic portfolios — requires a risk model (dynamic portfolios are keyed by its scores) */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="dynamicPortfoliosEnabled">{t.enableDynamicPortfolios}</Label>
                <p className="text-xs text-muted-foreground">
                  {riskModelId
                    ? t.enableDynamicPortfoliosHint
                    : t.enableDynamicPortfoliosRequiresRiskModel}
                </p>
              </div>
              <Switch
                id="dynamicPortfoliosEnabled"
                checked={formData.dynamicPortfoliosEnabled}
                onCheckedChange={(checked) => {
                  // Enabling on an active collection is rejected server-side —
                  // surface the reason immediately instead of on Save. Keyed on
                  // the saved value (not formData) so flipping back on after a
                  // local off-toggle stays possible when dynamic is already
                  // persisted as enabled.
                  if (checked && isCollectionActive && !dynamicPortfoliosEnabled) {
                    setError(t.cannotEnableDynamicWhileActive);
                    return;
                  }
                  setError(null);
                  // Switching dynamic portfolios off resets partial replacement
                  // server-side — mirror it so the disabled switch never shows on.
                  setFormData({
                    ...formData,
                    dynamicPortfoliosEnabled: checked,
                    dynamicPartialReplacementEnabled: checked
                      ? formData.dynamicPartialReplacementEnabled
                      : false,
                  });
                }}
                disabled={isPending || riskModelId == null}
              />
            </div>

            {/* Partial replacement (QL-584) — only settable while dynamic portfolios are on */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="dynamicPartialReplacementEnabled">
                  {t.allowPartialReplacement}
                </Label>
                <p className="text-xs text-muted-foreground">{t.allowPartialReplacementHint}</p>
              </div>
              <Switch
                id="dynamicPartialReplacementEnabled"
                checked={formData.dynamicPartialReplacementEnabled}
                onCheckedChange={(checked) => {
                  setError(null);
                  setFormData({ ...formData, dynamicPartialReplacementEnabled: checked });
                }}
                disabled={isPending || !formData.dynamicPortfoliosEnabled}
              />
            </div>

            {/* Model portfolio from another risk score — requires a risk model (candidates are keyed by its scores) */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="allowModelPortfolioRiskDeviation">
                  {t.allowModelPortfolioRiskDeviation}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {riskModelId
                    ? t.allowModelPortfolioRiskDeviationHint
                    : t.allowModelPortfolioRiskDeviationRequiresRiskModel}
                </p>
              </div>
              <Switch
                id="allowModelPortfolioRiskDeviation"
                checked={formData.allowModelPortfolioRiskDeviation}
                onCheckedChange={(checked) => {
                  setError(null);
                  setFormData({ ...formData, allowModelPortfolioRiskDeviation: checked });
                }}
                disabled={isPending || riskModelId == null}
              />
            </div>

            {/* Wrapper types (QP-178) — server blocks disabling while products are tagged */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="wrapperTypesEnabled">{t.enableWrapperTypes}</Label>
                <p className="text-xs text-muted-foreground">{t.enableWrapperTypesHint}</p>
              </div>
              <Switch
                id="wrapperTypesEnabled"
                checked={formData.wrapperTypesEnabled}
                onCheckedChange={(checked) => {
                  setError(null);
                  setFormData({ ...formData, wrapperTypesEnabled: checked });
                }}
                disabled={isPending}
              />
            </div>

            {/* Product types (QL-383) — server blocks disabling while products have a type */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="productTypesEnabled">{t.enableProductTypes}</Label>
                <p className="text-xs text-muted-foreground">{t.enableProductTypesHint}</p>
              </div>
              <Switch
                id="productTypesEnabled"
                checked={formData.productTypesEnabled}
                onCheckedChange={(checked) => {
                  setError(null);
                  setFormData({ ...formData, productTypesEnabled: checked });
                }}
                disabled={isPending}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" loading={isPending}>
              {t.save}
            </Button>
          </div>
        </form>
      </CardContent>

      <ConfirmDialog
        open={disableSustainabilityOpen}
        onOpenChange={setDisableSustainabilityOpen}
        title={t.disableSustainabilityTitle}
        description={t.disableSustainabilityDescription}
        confirmText={t.disable}
        cancelText={t.cancel}
        variant="destructive"
        onConfirm={confirmDisableSustainability}
      />

      <ConfirmDialog
        open={pendingRiskModelId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRiskModelId(null);
        }}
        title={t.assignRiskModelTitle}
        description={t.assignRiskModelDescription}
        confirmText={t.assign}
        cancelText={t.cancel}
        onConfirm={confirmAssignRiskModel}
      />
    </Card>
  );
}
